import { useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { FetchProductsResponse, MerchantProductsQuery, fetchProducts } from "../../products/productsApi"
import { PriceListType } from "../../priceList/priceListApi"
import { ProductGroupItem } from "../../productGroup/productGroupTypes"
import { ProductGroupSelector } from "../../products/components/tableElements/ProductGroupSelector"
import { PriceListSelector } from "../../products/components/tableElements/PriceListSelector"
import { SelectShop } from "../../shops/components/SelectShop"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { ProductOfferItem } from "../offersTypes"

function transformProductsData(data: FetchProductsResponse): ProductOfferItem[] {
    return data.productGroups.flatMap((group) => {
        const products = group.products.map<ProductOfferItem>((product) => ({
            ...product,
        }))

        return products
    })
}

export function Offers() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()

    const [priceListItem, setPriceListItem] = useState<PriceListType | null>(null)
    const [productGroupId, setProductGroupId] = useState<ProductGroupItem | null>(null)
    const [shopId, setShopId] = useState<string>()

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            shopId,
            ...(priceListItem && { priceListId: priceListItem.id }),
            ...(productGroupId && { productGroupId: productGroupId.id }),
        },
        select: transformProductsData,
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const columns = useMemo<MRT_ColumnDef<ProductOfferItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("offers-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-price-column"),
                    id: "price.amount",
                },
                (row) => row.price,
                true,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-discount-price-column"),
                    id: "offerDetails.discount.price.amount",
                },
                (row) => row?.offerDetails?.discount?.price,
                true,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-valid-from-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.from",
                },
                (row) => row?.offerDetails?.availabilityDetails.dateTimeInterval?.from,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-expires-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.to",
                },
                (row) => row?.offerDetails?.availabilityDetails.dateTimeInterval?.to,
            ),
            columnFactory.activeInactiveStatusColumn(
                { header: l10n.getString("offers-table-status-column"), id: "status" },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    return (
        <>
            <LocalizedStrict id="offers-page-header" attrs={{ title: true }}>
                <PageHeader title="Offers" page={Page.MERCHANT_ADMIN_OFFERS}>
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-offers">
                            <Breadcrumb>Offers</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <ProductGroupSelector
                                productGroupId={productGroupId}
                                handleChange={(el) => setProductGroupId(el)}
                            />
                            <SelectShop shopId={shopId} setShopId={setShopId} />
                            <PriceListSelector
                                priceListItem={priceListItem}
                                handleChange={(el) => setPriceListItem(el)}
                            />
                        </StandardTableFilter>
                    )}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="offers-table-edit-menu-item" key={`edit-${row.original.id}`}>
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                }}
                            >
                                Edit
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict
                            id="offers-table-remove-offer-menu-item"
                            key={`remove-offer-${row.original.id}`}
                        >
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                }}
                            >
                                Remove offer
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                />
            </Grid>
        </>
    )
}
